/**
 * Sezione "Etichette" nella sidebar: catalogo colorato, filtro per etichetta,
 * creazione (con scelta colore) ed eliminazione.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Tag, Plus, X, Loader2 } from 'lucide-react';
import { labelsApi } from '@/lib/api/endpoints';
import { useInbox } from '@/stores/inbox.store';
import { cn } from '@/lib/utils';

const PALETTE = ['#6366f1', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#64748b'];

export function LabelsSection() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const selectedLabel = useInbox((s) => s.selectedLabel);
  const setLabel = useInbox((s) => s.setLabel);

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState(PALETTE[0]);
  const [busy, setBusy] = useState(false);

  const labelsQuery = useQuery({ queryKey: ['labels'], queryFn: () => labelsApi.list() });
  const labels = labelsQuery.data?.labels ?? [];

  async function createLabel() {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await labelsApi.create(trimmed, color);
      await qc.invalidateQueries({ queryKey: ['labels'] });
      setName('');
      setColor(PALETTE[labels.length % PALETTE.length]);
      setAdding(false);
    } finally {
      setBusy(false);
    }
  }

  async function removeLabel(id: string, labelName: string) {
    await labelsApi.remove(id);
    if (selectedLabel === labelName) setLabel(null);
    await qc.invalidateQueries({ queryKey: ['labels'] });
    await qc.invalidateQueries({ queryKey: ['messages'] });
  }

  return (
    <div className="space-y-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-3 py-1 flex items-center justify-between">
        <span className="flex items-center gap-1.5"><Tag className="h-3 w-3" /> Etichette</span>
        <button
          type="button"
          onClick={() => { setAdding((v) => !v); setColor(PALETTE[labels.length % PALETTE.length]); }}
          className="hover:text-foreground"
          title="Nuova etichetta"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      {adding && (
        <div className="px-3 py-2 space-y-2">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void createLabel(); if (e.key === 'Escape') setAdding(false); }}
            placeholder="Nome etichetta"
            maxLength={60}
            className="w-full text-sm rounded-md border bg-background px-2 py-1 outline-none focus:ring-1 focus:ring-primary"
          />
          <div className="flex items-center gap-1.5">
            {PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={cn('h-4 w-4 rounded-full border', color === c && 'ring-2 ring-offset-1 ring-foreground')}
                style={{ backgroundColor: c }}
              />
            ))}
            <button
              type="button"
              onClick={() => void createLabel()}
              disabled={!name.trim() || busy}
              className="ml-auto text-xs font-medium text-primary disabled:opacity-40 inline-flex items-center gap-1"
            >
              {busy && <Loader2 className="h-3 w-3 animate-spin" />} Crea
            </button>
          </div>
        </div>
      )}

      {labels.map((l) => (
        <div
          key={l.id}
          className={cn(
            'group w-full flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors',
            selectedLabel === l.name ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted'
          )}
        >
          <button
            type="button"
            onClick={() => { setLabel(l.name); navigate('/'); }}
            className="flex items-center gap-3 flex-1 min-w-0 text-left"
          >
            <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
            <span className="flex-1 truncate">{l.name}</span>
          </button>
          <button
            type="button"
            onClick={() => void removeLabel(l.id, l.name)}
            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
            title="Elimina etichetta"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}

      {labels.length === 0 && !adding && (
        <p className="px-3 py-1 text-[11px] text-muted-foreground">Nessuna etichetta. Creane una col +</p>
      )}
    </div>
  );
}
